import { Modal, IconButton, Spinner, Badge } from '@components';
import {
  XMarkIcon,
  PencilSquareIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useDeal } from './context';
import { RenderAvatar } from '../utils/userAvatar';
import { useState, useEffect } from 'react';
import Message from '@components/message';
import axios from 'axios';

export default function AssignModal({
  accessType,
  taskItemIds = [],
  title = 'Quoi doit compléter ?',
  isOpen,
  onClose = () => {},
  defaultTemplateSlug
}) {
  const { dealId, dealMembers = [], authStatuses } = useDeal();
  const [assignType, setAssignType] = useState<'adding' | 'removing'>('adding');
  const [editing, setEditing] = useState(false);
  const [notify, setNotify] = useState(true);
  const templateSlug = assignType === 'adding' ? defaultTemplateSlug : 'DESAFFECTER_UN_INTERVENANT';
  const [userAccountIds, setUserAccountIds] = useState([]);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setEditing(false);
      setNotify(true);
      setAssignType('adding');
    }
  }, [isOpen, accessType]);

  function closeWithReset() {
    setUserAccountIds([]);
    setMessage('');
    setMessage(true);
    setEditing(false);
    setAssignType('adding');
    onClose();
  }

  function onSubmit(e) {
    e.preventDefault();
    setIsLoading(true);
    axios({
      method: 'put',
      url: `/deals/${dealId}/batch_assign`,
      data: {
        access_type: accessType,
        assign_type: assignType,
        task_item_ids: taskItemIds,
        user_account_ids: userAccountIds,
        notify: notify ? 1 : 0,
        message
      }
    })
      .then(({ data }) => {
        closeWithReset();
        Turbo.renderStreamMessage(data);
        setIsLoading(false);
      })
      .catch(err => {
        setIsLoading(false);
      });
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="bg-white rounded-lg shadow-lg lg:w-[600px] w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="py-3 px-6 flex justify-between border-b flex-none">
          <h3 className="font-hmedium tracking-tight text-xl">{title}</h3>
          <button
            onClick={onClose}
            className="w-6 h-6 rounded-full bg-slate-50 flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-slate-700"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Message Editor Section */}

        {/* Main Selection Section */}
        <div className={'flex flex-col flex-1 min-h-0'}>
          {/* Tabs */}
          <div className="grid grid-cols-2 border-b divide-x flex-none">
            <button
              onClick={e => {
                e.preventDefault();
                setAssignType('adding');
                setEditing(false);
              }}
              className={`cursor-pointer flex justify-center items-center py-2 font-hmedium space-x-2 opacity-50 hover:opacity-100 ${
                assignType === 'adding' ? 'bg-slate-50 !opacity-100' : ''
              }`}
            >
              <div className="flex items-center">
                <input readOnly type="radio" checked={assignType === 'adding'} />
              </div>
              <div>Ajouter</div>
            </button>
            <button
              onClick={e => {
                e.preventDefault();
                setAssignType('removing');
                setEditing(false);
              }}
              className={`cursor-pointer flex justify-center items-center py-2 font-hmedium space-x-2 opacity-50 hover:opacity-100 ${
                assignType === 'removing' ? 'bg-slate-50 !opacity-100' : ''
              }`}
            >
              <div className="flex items-center">
                <input readOnly type="radio" checked={assignType === 'removing'} />
              </div>
              <div>Retirer</div>
            </button>
          </div>

          {/* Scrollable Content */}

          <div className={`${editing && isOpen ? '' : 'hidden'} p-6`}>
            <Message
              onChange={setMessage}
              placeholder="Rédiger un message"
              defaultTemplateSlug={templateSlug}
              allowTemplates
              allowFiles
              allowSignature
            />
          </div>

          <div className={`${editing ? 'hidden' : 'flex-1'}  overflow-y-auto min-h-0`}>
            <div className="divide-y">
              {dealMembers.map(dm => {
                const subtitle = [dm.deal_member_type, dm.email, dm.mobile_phone]
                  .filter(str => !!str && str !== '')
                  .join(' - ');

                return (
                  <button
                    key={dm.id}
                    onClick={() => {
                      if (userAccountIds.includes(dm.user_account_id)) {
                        setUserAccountIds(prev => prev.filter(id => id !== dm.user_account_id));
                      } else {
                        setUserAccountIds(prev => [...prev, dm.user_account_id]);
                      }
                    }}
                    type="button"
                    className="w-full flex items-center space-x-2 px-6 py-3 hover:bg-slate-50"
                  >
                    <div className="w-full flex items-center space-x-2 hover:bg-slate-50">
                      <div className="w-8">
                        <input
                          readOnly
                          checked={userAccountIds.includes(dm.user_account_id)}
                          type="checkbox"
                        />
                      </div>
                      <RenderAvatar
                        authStatuses={authStatuses}
                        width="w-10 h-10 [&>[data-state]]:!w-10 [&>[data-state]]:!h-10"
                        pingSize="w-[10px] h-[10px]"
                        user={dm}
                      />
                      <div className="text-left">
                        <h2 className="font-hmedium tracking-tight">
                          {dm.first_name} {dm.last_name} {dm.name}
                        </h2>
                        <p className="text-sm text-slate-500">{subtitle}</p>
                      </div>
                    </div>
                    <div>
                      {!dm.email && !!notify && (
                        <Badge
                          icon={ExclamationTriangleIcon}
                          color="amber"
                          label="Ne sera pas notifié"
                        />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center px-6 py-2 border-t justify-between flex-none">
            <div className="flex items-center space-x-4">
              <label
                onClick={e => {
                  e.preventDefault();
                  setNotify(!notify);
                }}
                className="cursor-pointer flex items-center space-x-2 [&_*]:pointer-events-none"
              >
                <input readOnly checked={notify} type="checkbox" />
                <div className="text-xs font-hmedium">Oui, je veux notifier les intervenants</div>
              </label>

              {notify &&
                (editing ? (
                  <button
                    onClick={() => setEditing(false)}
                    type="button"
                    className="text-xs font-hmedium bg-blue-50 rounded-full text-blue-600 ring-1 ring-blue-600 flex items-center py-1 px-3"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                    <span>Retour sélection</span>
                  </button>
                ) : (
                  <div>
                    <button
                      type="button"
                      onClick={() => setEditing(true)}
                      className="text-xs font-hmedium bg-blue-50 rounded-full text-blue-600 ring-1 ring-blue-600 flex items-center py-1 px-3"
                    >
                      <PencilSquareIcon className="w-4 h-4" />
                      <span>Modifier le message</span>
                    </button>
                  </div>
                ))}
            </div>

            <div>
              <button
                type="submit"
                onClick={onSubmit}
                disabled={
                  !message ||
                  message === '' ||
                  isLoading ||
                  taskItemIds.length <= 0 ||
                  userAccountIds.length <= 0
                }
                className="bg-blue-600 space-x-2 rounded-lg text-white flex items-center font-hmedium px-3 py-2 disabled:opacity-50 disabled:pointer-events-none"
              >
                {isLoading ? <Spinner size="sm" /> : <ChevronRightIcon className="w-4 h-4" />}
                <span>Confirmer</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
